import { useEffect } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import { scaleSequential } from "d3-scale";
import { interpolateTurbo } from "d3-scale-chromatic";
import { makeStyles } from "@mui/styles";
import { getMetricParamName, getMetricUnits } from "./RTFOMetricsPanel";
import { getSeismicEventColor } from "./RTFOMapWithWellbore";


const useStyles = makeStyles(theme => ({
  legend: {
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    padding: '10px',
    borderRadius: '5px',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.4)',
    fontSize: '12px',
    fontFamily: 'Arial, Helvetica, sans-serif'
  },
  legendDark: {
    backgroundColor: '#37474f',
    padding: '10px',
    borderRadius: '5px',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.4)',
    fontSize: '12px',
    fontFamily: 'Arial, Helvetica, sans-serif'
  },
  legendTitle: {
    marginBottom: '5px',
    fontWeight: 'bold'
  },
  seismicLegendTitle: {
    marginTop: '10px',
    marginBottom: '5px',
    fontWeight: 'bold'
  },
  legendItem: {
    marginBottom: '5px',
    display: 'flex',
    alignItems: 'center'
  },
  seismicLegendItem: {
    marginBottom: '5px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer'
  },
  colorBox: {
    width: '15px',
    height: '15px',
    marginRight: '5px',
    borderRadius: '3px',
    boxShadow: '0px 0px 4px lightgrey'
  },
  seismicColorBox: {
    width: '15px',
    height: '15px',
    marginRight: '5px',
    borderRadius: '50%',
    boxShadow: '0px 0px 4px lightgrey',
    cursor: 'pointer'
  },
  seismicLayerColorBox: {
    width: '15px',
    height: '5px',
    marginRight: '5px',
    boxShadow: '0px 0px 4px lightgrey'
  },
  legendText: {
    whiteSpace: 'nowrap'
  }
}));

const RTFOMapLegendLegacy = ({ 
  minColorValue, 
  maxColorValue, 
  metric, 
  darkMode, 
  showMetric, 
  showSeismic, 
  seismicFilter=[], 
  setSeismicFilter,
  isBufferZoneHidden,
  setIsBufferZoneHidden,
  isFPZHidden,
  setIsFPZHidden
}) => {
  
  const map = useMap();
  const classes = useStyles();

  useEffect(() => {

    const colorScale = scaleSequential(interpolateTurbo).domain([minColorValue, maxColorValue]);

    let grades = [];
    let colors = [];

    if (metric === 'Seismicity Risk') {
      grades = [0, 1, 2, 3, 4, 5, 6];
      colors = ['white', 'yellow', 'peachpuff', 'gold', 'red', 'purple', 'grey'];
    } else {
      const steps = 10;
      const interval = (maxColorValue - minColorValue) / steps;
      for (let i = 0; i < steps; i++) {
        const value = minColorValue + i * interval;
        grades.push(parseFloat(value?.toFixed(2)));
        colors.push(colorScale(value));
      };
    };

    const legend = L.control({ position: "topright" });

    legend.onAdd = () => {
      const div = L.DomUtil.create("div", (darkMode ? classes.legendDark : classes.legend));
      if (showMetric) {
        let from;
        let to;
  
        const titleItem = L.DomUtil.create("div", classes.legendTitle, div);
        const titleText = L.DomUtil.create("span", classes.legendTitle, titleItem);
        titleText.innerHTML = 'Metrics Legend';
  
        for (let i = 0; i < grades.length; i++) {
          from = grades[i];
          to = grades[i + 1];
  
          if (!isNaN(from)) {
            const item = L.DomUtil.create("div", classes.legendItem, div);
            const colorBox = L.DomUtil.create("div", classes.colorBox, item);
            colorBox.style.backgroundColor = colors[i];
            const text = L.DomUtil.create("span", classes.legendText, item);
            text.innerHTML = `${from}${to ? " - " + to : "+"} ${metric === 'Stress Shadow' ? 'MPa' : getMetricUnits(getMetricParamName(metric))}`;
          };
        };
      };

      if (showSeismic) {
        const seismicItem = L.DomUtil.create("div", classes.seismicLegendTitle, div);
        const seismicText = L.DomUtil.create("span", classes.seismicLegendTitle, seismicItem);
        seismicText.innerHTML = 'Seismicity Legend';
  
        [0.75, 1.5, 2.5, 3.5, 4.5]?.forEach(seismicValue => {
          const item = L.DomUtil.create("div", classes.seismicLegendItem, div);
          const colorBox = L.DomUtil.create("div", classes.seismicColorBox, item);
          item.addEventListener('click', function() {
            seismicFilter.includes(seismicValue) ? setSeismicFilter(prev => prev.filter(d => d !== seismicValue)) : setSeismicFilter(prev => [...prev, seismicValue]);
          });
          item.style.opacity = seismicFilter.includes(seismicValue) ? 0.25 : 1;
          colorBox.style.backgroundColor = getSeismicEventColor(seismicValue);
          const text = L.DomUtil.create("span", classes.legendText, item);
          if (seismicValue === 0.75) {
            text.innerHTML = 'Local Magnitude < 1';
          } else if (seismicValue === 1.5) {
            text.innerHTML = 'Local Magnitude 1 - 2';
          } else if (seismicValue === 2.5) {
            text.innerHTML = 'Local Magnitude 2 - 3';
          } else if (seismicValue === 3.5) {
            text.innerHTML = 'Local Magnitude 3 - 4';
          } else if (seismicValue === 4.5) {
            text.innerHTML = 'Local Magnitude > 4';
          };
        });

        // add buffer zone legend
        L.DomUtil.create("div", classes.legendItem, div);
        const bufferItem = L.DomUtil.create("div", classes.legendItem, div);
        bufferItem.style.opacity = isBufferZoneHidden ? 0.25 : 1;
        bufferItem.style.cursor = 'pointer';
        bufferItem.addEventListener('click', function() {
          setIsBufferZoneHidden(prev => !prev);
        });
        const bufferColorBox = L.DomUtil.create("div", classes.seismicLayerColorBox, bufferItem);
        bufferColorBox.style.backgroundColor = 'red';
        const bufferText = L.DomUtil.create("span", classes.legendText, bufferItem);
        bufferText.innerHTML = 'Buffer Zone (5 km)'
      }

      // add fpz legend
      const fpzItem = L.DomUtil.create("div", classes.legendItem, div);
      fpzItem.style.opacity = isFPZHidden ? 0.25 : 1;
      fpzItem.style.cursor = 'pointer';
      fpzItem.addEventListener('click', function() {
        setIsFPZHidden(prev => !prev);
      });
      const fpzColorBox = L.DomUtil.create("div", classes.seismicLayerColorBox, fpzItem);
      fpzColorBox.style.backgroundColor = 'grey';
      const fpzText = L.DomUtil.create("span", classes.legendText, fpzItem);
      fpzText.innerHTML = 'FPZ'
      return div;
    };

    legend.addTo(map);

    return () => {
      legend.remove();
    };
  }, [classes.colorBox, classes.legend, classes.legendDark, classes.legendTitle, classes.legendItem, classes.legendText, classes.seismicLegendTitle, classes.seismicColorBox, darkMode, map, maxColorValue, metric, minColorValue, showSeismic, showMetric, classes.seismicLayerColorBox, seismicFilter, setSeismicFilter, classes.seismicLegendItem, isBufferZoneHidden, setIsBufferZoneHidden, setIsFPZHidden, isFPZHidden]);

  return null;
};

export default RTFOMapLegendLegacy;